import Progression from "./components/Progression.component";
import Welcome from "./components/Welcome/Welcome.component";
import DecisionPageController from "./DecisionPage.controller";
import moment from "moment";
import "./DecisionPage.styles.scss";
import { ButtonDS, GlobalIcon } from "get-life-storybook-ts";
import numberFormat, { renderUnderlined } from "../../utils/Utils";
import DecisionPageSQLLayout from "./layouts/DecisionPageSQLLayout";
import { CapitalsResponse } from "../../api/request/Lead/Interfaces/DataInterface.response";
import { TraitName } from "../../api/request/Lead/Interfaces/TraitTypes";

const TRAIT_LABEL_KEY: Record<TraitName, string> = {
  DEATH: "payment.policyDetails.death",
  DISABILITY: "payment.multiproduct.disability",
  SERIOUS_ILLNESS: "payment.multiproduct.seriousIllness",
};

const getActiveTraits = (capitals?: CapitalsResponse): TraitName[] =>
  capitals
    ? (Object.keys(capitals) as TraitName[]).filter(
        (key) => capitals[key]?.active
      )
    : [];

const DecisionPage = (): JSX.Element => {
  const {
    data,
    progression,
    translate,
    continueProcess,
    resetQuote,
    brokerPhone,
    isSqlDomain,
  } = DecisionPageController();

  if (isSqlDomain) {
    return (
      <DecisionPageSQLLayout
        data={data}
        progression={progression}
        translate={translate}
        continueProcess={continueProcess}
        brokerPhone={brokerPhone ?? undefined}
      />
    );
  }

  const capitals: CapitalsResponse | undefined = data?.capitals;
  const activeTraits = getActiveTraits(capitals);

  return (
    <>
      <div className="decisionPage backGround-white">
        <div className="welcome-container">
          <Welcome
            name={data?.name}
            email={data?.email}
            date={moment(data?.birthDate).format("DD-MM-YYYY")}
            city={data?.province}
          />
        </div>

        <div className="decisionPage-container">
          <div className="flex items-center bg-white justify-center text-purple">
            <div className="w-11/12 md:w-8/12 md:max-w-[740px] bg-purple border-purple border-2 border-solid rounded-t-xl rounded-b-md">
              <div className="bg-purple font-DM text-white text-left text-sm p-3 px-5 border-solid rounded-t-xl">
                {translate("decision.resume")}
              </div>
              <div className="border-solid border-t-2 border-purple rounded-bl-md rounded-br-md bg-white p-5 md:p-10 flex flex-col gap-[20px]">
                <p className="font-DM text-sm text-purple" style={{marginBottom: 16}}>
                  {translate("payment.policyDetails.subtitle")}
                </p>
                <ul className="flex flex-col gap-[12px] !m-0 !p-0 list-none">
                  {activeTraits.map((trait) => (
                    <li
                      key={trait}
                      className="flex items-start gap-[10px]"
                    >
                      <div className="shrink-0 mt-[2px]">
                        <GlobalIcon
                          iconName="FilledCheckIcon"
                          size="XS"
                          color="var(--theme-primary)"
                        />
                      </div>
                      <p className="font-DM text-sm text-purple !m-0">
                        <span>{translate(TRAIT_LABEL_KEY[trait])}</span>
                        <span className="font-bold">
                          {": "}
                          {numberFormat(
                            capitals?.[trait]?.capital ?? 0,
                            "capital"
                          )}
                        </span>
                      </p>
                    </li>
                  ))}
                </ul>
                {data?.intention && (
                  <p className="text-sm text-[#26313780] !m-0">
                    {renderUnderlined(data?.intention, translate)}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="progression-container">
            <Progression
              title={translate("decisionPage.Progression.title")}
              list={progression}
              type=""
            />
          </div>
          <div className="continue-container">
            <p className="text-continue">
              {translate("decision.continue.question")}
            </p>
            <ButtonDS
              rightIcon="ArrowGoRightIcon"
              label={translate("decision.continue.button")}
              onClick={() => continueProcess()}
            />
            {
              (data?.telesusc === false || !data?.telesusc) && (
                <ButtonDS
                  buttonType="secondary"
                  label={translate("decision.reset.button")}
                  onClick={() => resetQuote()}
                />
              )
            }

            <p className="text-continue">
              {translate("decision.call.question")} <br></br>{" "}
              <a href={`tel:${process.env.REACT_APP_TEL}`}>
                {translate("decision.call.link")} {process.env.REACT_APP_TEL}
              </a>{" "}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default DecisionPage;
